import type { Asset } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import type { useSweetpassTier } from '@order/hooks';

import { getHomeScreenHeroCardContentTag } from './utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useHomeScreenHeroCardContent = (
  props: UseShortHomeScreenActionCardProps,
) => {
  const { hasActiveOrder, sweetpassTierName, shouldPauseContentLoading } =
    props;

  const contentTag = getHomeScreenHeroCardContentTag({
    sweetpassTierName,
    hasActiveOrder,
  });

  const singleSideCard =
    useContentfulContentTypeEntry<HeroCardContentSingleSided>({
      contentType: CONTENT_TYPE_ID,
      tags: [contentTag],
      pause: shouldPauseContentLoading || hasActiveOrder,
    });

  const doubleSidedCard =
    useContentfulContentTypeEntry<HeroCardContentDoubleSided>({
      contentType: CONTENT_TYPE_ID,
      tags: [contentTag],
      pause: shouldPauseContentLoading || !hasActiveOrder,
    });

  return { singleSideCard, doubleSidedCard };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE_ID = 'heroCard';

// ─── Types ───────────────────────────────────────────────────────────────────

type UseShortHomeScreenActionCardProps = {
  readonly hasActiveOrder: boolean;
  readonly shouldPauseContentLoading: boolean;
  readonly sweetpassTierName: ReturnType<
    typeof useSweetpassTier
  >['sweetpassNextTierName'];
};

type HeroCardContentSingleSided = {
  readonly cardType: 'single-sided' | 'double-sided';

  readonly titleFrontSide: string;
  readonly imageFrontSide: Asset;
  readonly ctaLabelFrontSide: string;
  readonly ctaUrlFrontSide?: string; // URL
};

type HeroCardContentDoubleSided = {
  readonly titleBackSide: string;
  readonly descriptionBackSide: string;
  readonly inlineLinkUrlBackSide: string; // URL
  readonly inlineLinkLabelBackSide: string;
  readonly ctaLabelBackSide: string;
} & HeroCardContentSingleSided;
